import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface CardAccount {
  id: string;
  brand: string | null;
  last4: string | null;
  currency: string;
  limit_cents: string | null;
  status: 'active' | 'frozen' | 'closed';
  provider: 'stripe' | 'gmo-pg';
  user_id: string | null;
  created_at: string;
}

export interface CardTx {
  id: string;
  card_id: string;
  occurred_at: string;
  merchant: string | null;
  mcc: string | null;
  amount_cents: string;
  currency: string;
  status: 'authorized' | 'captured' | 'refunded' | 'disputed';
}

export function useCards(wsId: string | undefined) {
  return useQuery<CardAccount[]>({
    queryKey: ['cards', wsId],
    enabled: !!wsId,
    queryFn: async () => (await api.get<CardAccount[]>(`/workspaces/${wsId}/cards`)).data,
  });
}

export function useCardTransactions(wsId: string | undefined) {
  return useQuery<CardTx[]>({
    queryKey: ['cards', wsId, 'transactions'],
    enabled: !!wsId,
    queryFn: async () => (await api.get<CardTx[]>(`/workspaces/${wsId}/cards/transactions`)).data,
  });
}

export function useCreateCard(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      provider: 'stripe' | 'gmo-pg';
      brand?: string;
      last4?: string;
      currency: string;
      limitCents?: number;
    }) => (await api.post<CardAccount>(`/workspaces/${wsId}/cards`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards', wsId] }),
  });
}

export function useRecordCardTx(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      cardId: string;
      occurredAt: string;
      merchant?: string;
      mcc?: string;
      amountCents: number;
      currency: string;
      status: 'authorized' | 'captured' | 'refunded' | 'disputed';
    }) => (await api.post<CardTx>(`/workspaces/${wsId}/cards/transactions`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cards', wsId, 'transactions'] }),
  });
}
